    import React from "react";
import { motion } from "framer-motion";

// Palette gold / white (identique à votre galerie)
const colors = {
  goldLight: '#F5E1C8',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

/**
 * Composant d'affichage de vidéos YouTube
 * @param {Object[]} videos - Tableau d'objets { id: string, title: string }
 * @param {string} title - Titre de la section (optionnel)
 */
const YouTubeVideos = ({ 
  videos = [], 
  title = "NOS VIDÉOS" 
}) => {
  // IDs par défaut si aucun tableau n'est fourni (exemples)
  const defaultVideos = [
    { id: "9hnnH-xBmdc", title: "Azyekos-Services - Votre Concièrgerie" },
    { id: "ByUdf3fodho", title: "Azyekos-Lounges - Airport Lounge" },
    { id: "KA5Jw4QEW9E", title: "Azyekos-Services - Votre Concièrgerie de lux" },
    { id: "w1nOwnZ0Ex8", title: "Azyekos Services  - 5 Ans d’Excellence et d’Innovation" },
     { id: "XDDdGFeeNtQ", title: "EVENT ALEXIA WAKU - LES 5 ANS AZYEKOS" },

  ];

  const displayVideos = videos.length > 0 ? videos : defaultVideos;

  return (
    <>
      <style>{`
        .youtube-section {
          position: relative;
          padding: 80px 0;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradient 15s ease infinite;
          overflow: hidden;
        }

        @keyframes goldGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .youtube-section::before,
        .youtube-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.3;
          z-index: 0;
          pointer-events: none;
        }
        .youtube-section::before {
          width: 50vw; height: 50vw;
          background: #F5E1C8;
          top: -10%; left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }
        .youtube-section::after {
          width: 40vw; height: 40vw;
          background: #E8CFB5;
          bottom: -8%; right: -4%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container-youtube {
          position: relative;
          z-index: 2;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .youtube-title {
          font-size: clamp(2rem, 5vw, 2.8rem);
          font-weight: 700;
          color: ${colors.text};
          text-align: center;
          margin-bottom: 0.5rem;
          letter-spacing: -0.5px;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.goldDark}, ${colors.gold});
          margin: 0 auto 40px;
          border-radius: 2px;
        }

        .grid-youtube {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        .video-card {
          position: relative;
          background: ${colors.glassBg};
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 20px;
          overflow: hidden;
          box-shadow: ${colors.shadow};
          transition: all 0.3s ease;
        }

        .video-card:hover {
          transform: translateY(-4px);
          box-shadow: ${colors.shadowHover};
          border-color: ${colors.gold};
        }

        .video-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%; /* 16:9 */
          overflow: hidden;
        }

        .video-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }

        .video-info {
          padding: 16px 20px;
        }

        .video-info h3 {
          margin: 0;
          font-size: 1.1rem;
          color: ${colors.text};
          font-weight: 600;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .grid-youtube {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .video-card {
            border-radius: 16px;
          }
        }
      `}</style>

      <section className="youtube-section">
        <div className="container-youtube">
          <motion.h2
            className="youtube-title"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {title}
          </motion.h2>
          <motion.div
            className="title-underline"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          />

          <motion.div
            className="grid-youtube"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {displayVideos.map((video, index) => (
              <motion.div
                key={video.id}
                className="video-card"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                transition={{ duration: 0.4 }}
              >
                <div className="video-wrapper">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="video-info">
                  <h3>{video.title}</h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default YouTubeVideos;